import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { formatAvailabilityDate, type QuarterInfo } from '../../../lib/fiscal-year';
import { cn } from '../../../lib/utils';
import { formatDateUK } from '../../../lib/date-utils';
import type { ProjectEvaluation } from '../../../api/shared/types';
import { Spinner } from '../../ui/spinner';

export interface ProjectCardData {
  epicCount: number;
  status: 'not-started' | 'in-progress' | 'submitted' | 'approved' | 'rejected' | 'reopened' | null;
  capexPercentage?: number;
  rndPercentage?: number;
  submittedBy?: string;
  submittedByName?: string;
  submittedAt?: string;
  approvedBy?: string;
  approvedByName?: string;
  approvedAt?: string;
}

interface ProjectCardProps {
  quarterInfo: QuarterInfo;
  projectName: string;
  data: ProjectCardData | null;
  onClick?: () => void;
  projectEvaluation?: ProjectEvaluation | null;
  disabledReason?: string;
  epicCountsLoading?: boolean;
  statusLoading?: boolean;
}

const statusConfig = {
  'not-started': null,
  'in-progress': null,
  submitted: null,
  approved: {
    label: 'Approved',
    className: 'bg-green-100 text-green-800 hover:bg-green-100',
  },
  rejected: null,
  reopened: {
    label: 'Re-opened',
    className: 'bg-blue-100 text-blue-800 hover:bg-blue-100',
  },
};

export function ProjectCard({
  quarterInfo,
  projectName,
  data,
  onClick,
  projectEvaluation,
  disabledReason,
  epicCountsLoading,
  statusLoading,
}: ProjectCardProps) {
  const { isAvailable, label, endDate } = quarterInfo;
  const isClickable = isAvailable && onClick;

  if (!isAvailable) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Card className="relative opacity-40 cursor-not-allowed">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold">{label}</CardTitle>
                <p className="text-sm text-muted-foreground">{projectName}</p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-sm text-muted-foreground"></div>
                <div className="absolute inset-0 flex items-center justify-center rounded-lg">
                  <p className="text-sm font-medium text-center px-4">
                    Available after {formatAvailabilityDate(endDate)}
                  </p>
                </div>
              </CardContent>
            </Card>
          </TooltipTrigger>
          <TooltipContent>
            <p>This quarter will become available after {formatAvailabilityDate(endDate)}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (!onClick && disabledReason) {
    const isZeroEpics = data?.epicCount === 0 && disabledReason === 'No closed items in this quarter';
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Card className={cn('relative cursor-not-allowed', isZeroEpics ? 'opacity-60' : 'opacity-40')}>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold text-muted-foreground">{label}</CardTitle>
                <div className="flex items-start justify-between">
                  <p className="text-sm text-muted-foreground">{projectName}</p>
                  <div className="flex items-center gap-1.5">{statusLoading && <Spinner className="size-4" />}</div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {isZeroEpics ? (
                  <div>
                    {epicCountsLoading && <Spinner className="size-4" />}
                    {!epicCountsLoading && <p className="text-2xl font-bold text-muted-foreground">0</p>}
                    <p className="text-sm text-muted-foreground">Closed Items</p>
                  </div>
                ) : (
                  <>
                    <div className="text-sm text-muted-foreground">--</div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <p className="text-sm font-medium text-center px-4">{disabledReason}</p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TooltipTrigger>
          <TooltipContent>
            <p>{disabledReason}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const epicCount = data?.epicCount ?? 0;
  const status = data?.status ?? 'not-started';
  const safeStatus = status === null ? 'not-started' : status;
  const statusInfo = statusConfig[safeStatus] ?? statusConfig['not-started'];

  return (
    <Card
      className={cn(
        'transition-all duration-200',
        isClickable ? 'cursor-pointer hover:shadow-lg hover:-translate-y-1' : 'cursor-default',
      )}
      onClick={isClickable ? onClick : undefined}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg font-semibold">{label}</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">{projectName}</p>
          </div>
          <div className="flex items-center gap-1.5">
            {statusLoading && <Spinner className="size-4" />}
            {!statusLoading && statusInfo && <Badge className={statusInfo.className}>{statusInfo.label}</Badge>}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <div className="flex items-center gap-2">
            {!epicCountsLoading && <p className="text-2xl font-bold">{epicCountsLoading ? '—' : epicCount}</p>}
            {epicCountsLoading && <Spinner className="size-5" />}
          </div>
          <p className="text-sm text-muted-foreground">Closed Items</p>
        </div>

        {/* != null guards against both null and undefined (API can return null, not undefined) */}
        {status === 'approved' && data?.capexPercentage != null && data?.rndPercentage != null && (
          <div className="pt-3 border-t space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">CapEx</span>
              <span className="text-sm font-semibold">{data.capexPercentage.toFixed(0)}%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">R&D</span>
              <span className="text-sm font-semibold">{data.rndPercentage.toFixed(0)}%</span>
            </div>
          </div>
        )}

        {projectEvaluation && (
          <div className="pt-3 border-t space-y-2">
            <p className="text-xs font-medium text-muted-foreground">Project Evaluation</p>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">CapEx</span>
              <span className="text-sm font-semibold">{(projectEvaluation.capex_percentage ?? 0).toFixed(0)}%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">R&D</span>
              <span className="text-sm font-semibold">{(projectEvaluation.rnd_percentage ?? 0).toFixed(0)}%</span>
            </div>
          </div>
        )}

        {status === 'approved' && data?.approvedByName && (
          <div className="pt-3 border-t space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Approved by</span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-sm font-semibold cursor-default">{data.approvedByName}</span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{data.approvedBy}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            {data?.approvedAt && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Approved on</span>
                <span className="text-sm font-semibold">
                  {formatDateUK(data.approvedAt)}
                </span>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
